/**
 * Shared "bulk action eligibility" calculation used by every participant tab's toolbar
 * (Invited, Registered, Accepted, Unattended, Attended).
 *
 * A bulk action button is only ever shown when it applies to the *entire* current selection —
 * i.e. the intersection of each individually-selected participant's set of eligible actions,
 * not just some of them. Mixing in a single ineligible row (e.g. an already-attended participant
 * for "Set as attended", or a non-Speaker/Host role for "Ask for presentation") hides the bulk
 * button entirely rather than silently applying to a partial subset of the selection, which
 * would be surprising for the organiser clicking it.
 */

/**
 * Computes the ids eligible for a single bulk action given the current selection and that
 * action's per-row eligibility predicate. Returns the full list of selected ids only when the
 * selection is non-empty and *every* selected row satisfies `isEligible` (a true set
 * intersection across the selection); otherwise returns an empty array, which callers use to
 * hide the corresponding toolbar button.
 */
export function intersectEligibleIds<T>(
  selectedRows: T[],
  getId: (row: T) => string,
  isEligible: (row: T) => boolean
): string[] {
  return selectedRows.length > 0 && selectedRows.every(isEligible) ? selectedRows.map(getId) : []
}
